import {CheckOutlined, FilterOutlined} from '@ant-design/icons';
import React, {useEffect, useRef, useState} from 'react';

import {TAG_COLORS} from '../../../consts/notifications/notifications';
import {IconChevronBottomBlack} from '../../Icons';
import NotificationCategoryIcon from '../NotificationCategoryIcon';

const OPTIONS = [{label: 'Усі категорії', value: null, color: '#0875d1'}].concat(
    Object.entries(TAG_COLORS).map(([label, color]) => ({label, value: label, color}))
);

const MobileNotificationTagFilter = ({
    selectedTag,
    categoryCounts = {},
    totalCount = 0,
    onTagChange,
    onReadAllClick,
}) => {
    const [isOpened, setIsOpened] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef = useRef(null);
    const optionRefs = useRef([]);
    const selectedIndex = Math.max(
        0,
        OPTIONS.findIndex((option) => option.value === selectedTag)
    );
    const selectedOption = OPTIONS[selectedIndex];
    const selectedCount = selectedTag === null ? totalCount : categoryCounts[selectedTag] || 0;

    const moveToOption = (index) => {
        setActiveIndex(index);
        const option = optionRefs.current[index];
        const list = option?.parentElement;

        // scrollIntoView can scroll the drawer or the page on phones.
        if (!list) return;
        if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
        else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
        }
    };

    useEffect(() => {
        if (!isOpened) return undefined;

        moveToOption(selectedIndex);
        const handleOutsidePress = (event) => {
            if (!rootRef.current?.contains(event.target)) setIsOpened(false);
        };

        document.addEventListener('pointerdown', handleOutsidePress);

        return () => document.removeEventListener('pointerdown', handleOutsidePress);
    }, [isOpened]);

    const selectOption = (index) => {
        onTagChange(OPTIONS[index].value);
        setIsOpened(false);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpened) {
            event.preventDefault();
            event.stopPropagation();
            setIsOpened(false);
            return;
        }
        if (event.key === 'Tab') {
            setIsOpened(false);
            return;
        }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            if (!isOpened) {
                setIsOpened(true);
                return;
            }
            const nextIndex =
                event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                    ? OPTIONS.length - 1
                    : Math.max(0, Math.min(OPTIONS.length - 1, activeIndex + (event.key === 'ArrowDown' ? 1 : -1)));

            moveToOption(nextIndex);
        } else if ((event.key === 'Enter' || event.key === ' ') && isOpened) {
            event.preventDefault();
            selectOption(activeIndex);
        } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const matches = OPTIONS.map((option, index) =>
                option.label.toLocaleLowerCase().startsWith(event.key.toLocaleLowerCase()) ? index : -1
            ).filter((index) => index >= 0);
            const match = matches.find((index) => index > activeIndex) ?? matches[0];

            if (match !== undefined) {
                event.preventDefault();
                if (!isOpened) onTagChange(OPTIONS[match].value);
                else moveToOption(match);
            }
        }
    };

    return (
        <div className='notification-filter' ref={rootRef}>
            <div className='notification-filter-toolbar'>
                <button
                    type='button'
                    role='combobox'
                    aria-label='Категорії сповіщень'
                    aria-haspopup='listbox'
                    aria-expanded={isOpened}
                    aria-controls={isOpened ? 'notification-category-options' : undefined}
                    aria-activedescendant={isOpened ? `notification-category-${activeIndex}` : undefined}
                    className='notification-category-trigger'
                    onClick={() => setIsOpened((opened) => !opened)}
                    onKeyDown={handleKeyDown}
                >
                    <span
                        className='notification-category-marker'
                        style={{'--tag-color': selectedOption.color}}
                        aria-hidden
                    />
                    <span className='notification-category-label'>{selectedOption.label}</span>
                    <span className='notification-category-count' data-empty={!selectedCount}>
                        {selectedCount}
                    </span>
                    <IconChevronBottomBlack aria-hidden className={isOpened ? 'rotate-180' : ''} />
                </button>

                <button type='button' className='notification-read-all' onClick={onReadAllClick}>
                    <CheckOutlined aria-hidden />
                    <span>Прочитати всі</span>
                </button>
            </div>
            {isOpened && (
                <div
                    id='notification-category-options'
                    className='notification-category-options'
                    role='listbox'
                    aria-label='Категорії сповіщень'
                >
                    {OPTIONS.map((option, index) => (
                        <div
                            key={option.label}
                            id={`notification-category-${index}`}
                            role='option'
                            aria-selected={option.value === selectedTag}
                            className={`notification-category-option${
                                activeIndex === index ? ' notification-category-option-active' : ''
                            }`}
                            style={{'--tag-color': option.color, '--tag-tint': `${option.color}18`}}
                            ref={(element) => (optionRefs.current[index] = element)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectOption(index)}
                        >
                            <span className='notification-category-option-icon'>
                                {option.value ? (
                                    <NotificationCategoryIcon category={option.value} />
                                ) : (
                                    <FilterOutlined aria-hidden />
                                )}
                            </span>
                            <span className='notification-category-option-label'>{option.label}</span>
                            <span
                                className='notification-category-count'
                                data-empty={!(option.value === null ? totalCount : categoryCounts[option.value])}
                            >
                                {option.value === null ? totalCount : categoryCounts[option.value] || 0}
                            </span>
                            <span className='notification-category-check' aria-hidden>
                                {option.value === selectedTag && <CheckOutlined />}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MobileNotificationTagFilter;
